import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { actionObjection } from "../../../../api/objectionAPI";
import logger from "../../../../utils/logger";
import type { ActionType, Objection } from "./types";

interface ObjectionActionModalProps {
  objection: Objection;
  actionType: ActionType;
  onClose: () => void;
  onCompleted: () => void;
}

export const ObjectionActionModal: React.FC<ObjectionActionModalProps> = ({
  objection,
  actionType,
  onClose,
  onCompleted,
}) => {
  const [remark, setRemark] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAction = async () => {
    try {
      setIsSubmitting(true);

      const res = await actionObjection(objection._id, {
        type: actionType,
        reason: remark,
      });
      const data = res?.data;

      if (data?.status) {
        toast.success(data?.message || "Request updated successfully");
        onClose();
        onCompleted();
      } else {
        toast.error(data?.message || "Something went wrong");
      }
    } catch (error: any) {
      toast.error(error?.message);
      logger.log("error", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6">
          <p className="text-gray-900 mb-4 text-lg">
            Are you sure you want to {actionType} the objection request?
          </p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Remark (Optional)
            </label>
            <textarea
              value={remark}
              onChange={(event) => setRemark(event.target.value)}
              placeholder="Add a remark or reason..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg border border-gray-300 text-gray-700 ${
                isSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-100"
              }`}
              onClick={() => {
                if (isSubmitting) return;
                onClose();
              }}
            >
              Cancel
            </button>
            <button
              disabled={isSubmitting}
              onClick={handleAction}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white transition ${
                actionType === "approve"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              } ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting
                ? actionType === "approve"
                  ? "Approving..."
                  : "Rejecting..."
                : actionType === "approve"
                  ? "Approve"
                  : "Reject"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
